/**
 * cart-drawer.js is lazy-loaded (theme.liquid). Until it runs, <cart-drawer> has no renderContents().
 * Ensures the custom element is defined before opening the drawer after add-to-cart.
 */
// Guard: prevent re-declaration if this script is loaded twice (some Shopify apps do this)
if (window.__productFormJsLoaded) {
  /* already loaded - skip */
} else {
  window.__productFormJsLoaded = true;

// Single shared promise prevents duplicate script loads when called concurrently
window._cartDrawerReadyPromise = window._cartDrawerReadyPromise || null;
async function ensureCartDrawerReady() {
  const el = document.querySelector('cart-drawer');
  if (!el) return;
  if (customElements.get('cart-drawer')) return;
  if (window._cartDrawerReadyPromise) return window._cartDrawerReadyPromise;
  window._cartDrawerReadyPromise = (async () => {
    const url = window.cartDrawerScriptUrl;
    if (!url) return;
    const already = Array.from(document.querySelectorAll('script[src]')).find((s) => s.src.includes('cart-drawer'));
    if (!already) {
      await new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = url;
        s.defer = true;
        s.onload = resolve;
        s.onerror = reject;
        document.body.appendChild(s);
      });
    }
    await Promise.race([
      customElements.whenDefined('cart-drawer'),
      new Promise((_, reject) => setTimeout(() => reject(new Error('cart-drawer define timeout')), 5000))
    ]);
  })();
  // If this attempt fails, drop the cached rejected promise so a later
  // caller can retry instead of inheriting the same rejection forever.
  window._cartDrawerReadyPromise.catch(() => {
    window._cartDrawerReadyPromise = null;
  });
  return window._cartDrawerReadyPromise;
}

async function refreshCartElement() {
  try {
    if (document.querySelector('cart-drawer')) {
      await ensureCartDrawerReady();
    }
  } catch (e) {
    console.warn('[product-form] cart drawer script:', e);
  }
  return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      if (!this.form) return;

      const idInput = this.form.querySelector('[name=id]');
      if (idInput) idInput.disabled = false;

      // Store bound handler so disconnectedCallback can remove it.
      // Required for memory-leak cleanup when sections are swapped via
      // the Section Rendering API (cart drawer re-render, Theme Editor
      // reload, our complementary section's lazy fetch, etc.).
      this._boundSubmit = this.onSubmitHandler.bind(this);
      // The lazy module can register after Shopify's native submit observer.
      // Capture prevents the native form event before its bubble listener,
      // leaving the successful AJAX response as the single add-event owner.
      this.form.addEventListener('submit', this._boundSubmit, true);
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.submitButton = this.querySelector('[type="submit"]');
      if (this.submitButton && document.querySelector('cart-drawer')) {
        this.submitButton.setAttribute('aria-haspopup', 'dialog');
      }

      // Warm drawer assets on purchase intent; native add needs no merge read.
      this._boundWarmCartDrawer = this.warmCartDrawer.bind(this);
      if (this.submitButton) {
        ['pointerdown', 'touchstart'].forEach((eventName) => {
          this.submitButton.addEventListener(eventName, this._boundWarmCartDrawer, { passive: true });
        });
      }

      this.hideErrors = this.dataset.hideErrors === 'true';
      this._isSubmitting = false; // Prevent double-clicks

      // Set up live validation on quantity input
      this.setupQuantityValidation();
    }

    warmCartDrawer() {
      if (typeof window.__loadCartDrawerCSS === 'function') window.__loadCartDrawerCSS();
      const coreLoader = window.__loadCartDrawerCore || window.__loadCartDrawerFully;
      if (typeof coreLoader === 'function') {
        coreLoader().catch(() => null);
      }
    }

    /* Start visible feedback independently from Shopify's cart mutation.
       The cart response remains authoritative; this only opens a stable shell
       and shows its adding state while /cart/add.js or /cart/change.js runs. */
    beginCartDrawerFeedback(item) {
      if (this.dataset.autoOpenCartDrawer === 'false') return Promise.resolve(null);

      const host = document.querySelector('cart-drawer');
      if (!host) return Promise.resolve(null);

      this._cartFeedbackWasActive = host.classList.contains('active');
      this._cartFeedbackOpened = false;
      host.classList.add('cart-drawer--pending-open');
      host.setAttribute('aria-busy', 'true');

      const coreLoader = window.__loadCartDrawerCore || window.__loadCartDrawerFully;
      const ready = (async () => {
        try {
          if (typeof coreLoader === 'function') await coreLoader();
          await ensureCartDrawerReady();

          const drawer = document.querySelector('cart-drawer');
          if (!drawer) return null;
          this.cart = drawer;
          drawer.classList.remove('cart-drawer--pending-open');

          if (!drawer.classList.contains('active') && typeof drawer.open === 'function') {
            drawer.open(this.submitButton || null);
            this._cartFeedbackOpened = true;
          }
          if (typeof drawer.showAddingState === 'function') drawer.showAddingState(item);

          /* Enhancements are not needed to show the cart shell. Load them in
             the background so they cannot delay the first visible response. */
          if (typeof window.__loadCartDrawerFully === 'function') {
            window.__loadCartDrawerFully().catch(() => null);
          }
          return drawer;
        } catch (error) {
          host.classList.remove('cart-drawer--pending-open');
          host.removeAttribute('aria-busy');
          console.warn('[product-form] cart feedback unavailable:', error);
          return null;
        }
      })();

      this._cartFeedbackPromise = ready;
      return ready;
    }

    /* The variant id is enough to reuse an existing cart line's quantity
       loading state. New lines use the drawer's single centered overlay. */
    getCartFeedbackItem() {
      return {
        variantId: String(this.form.querySelector('[name="id"]')?.value || ''),
      };
    }

    cancelCartDrawerFeedback() {
      const pending = this._cartFeedbackPromise;
      this._cartFeedbackPromise = null;
      const host = document.querySelector('cart-drawer');
      if (host) {
        host.classList.remove('cart-drawer--pending-open');
        host.removeAttribute('aria-busy');
      }

      const cleanup = (drawer) => {
        if (!drawer) return;
        if (typeof drawer.hideAddingState === 'function') drawer.hideAddingState();
        if (this._cartFeedbackOpened && !this._cartFeedbackWasActive && typeof drawer.close === 'function') {
          drawer.close();
        }
        this._cartFeedbackOpened = false;
      };
      if (pending) pending.then(cleanup).catch(() => null);
      else cleanup(host);
    }

    async getReadyCartDrawer() {
      const drawer = this._cartFeedbackPromise
        ? await this._cartFeedbackPromise.catch(() => null)
        : null;
      this._cartFeedbackPromise = null;
      return drawer || refreshCartElement();
    }

    adjustCartBubble(delta) {
      if (!delta) return;
      document
        .querySelectorAll(
          '#cart-icon-bubble .wbhcartitem, #cart-icon-bubble .header__cart-count, [data-cart-count]'
        )
        .forEach((node) => {
          const current = parseInt((node.textContent || '').trim(), 10) || 0;
          const next = Math.max(0, current + delta);
          node.textContent = next;
          node.classList.toggle('hidden', next === 0);
        });
    }

    settleCartBubble(data) {
      const html = data && data.sections && data.sections['cart-icon-bubble'];
      if (!html) {
        this._optimisticCartDelta = 0;
        return;
      }
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      const countNode = tmp.querySelector('.wbhcartitem, .header__cart-count');
      if (countNode) {
        const count = parseInt((countNode.textContent || '').trim(), 10) || 0;
        if (typeof window.updateCartCount === 'function') window.updateCartCount(count);
        document
          .querySelectorAll(
            '#cart-icon-bubble .wbhcartitem, #cart-icon-bubble .header__cart-count, [data-cart-count]'
          )
          .forEach((node) => {
            node.textContent = count;
            node.classList.toggle('hidden', count === 0);
          });
      }
      this._optimisticCartDelta = 0;
    }

    rollbackOptimisticCartBubble() {
      if (!this._optimisticCartDelta) return;
      this.adjustCartBubble(-this._optimisticCartDelta);
      this._optimisticCartDelta = 0;
    }

    getCartRenderState(state) {
      state = Object.assign({}, state, window.cartSnapshotFromState?.(state));
      if (this.dataset.autoOpenCartDrawer !== 'false' || !state) return state;
      return { ...state, skipDrawerOpen: true };
    }

    prepareCartDrawerForPurchase() {
      if (this.dataset.openCartExpress !== 'true') return;
      const drawer = this.cart || document.querySelector('cart-drawer');
      if (!drawer) return;
      drawer.dataset.currentStep = 'cart';
      delete drawer.dataset.expressCheckoutOpen;
      delete drawer.dataset.expressCartCollapsedPref;
      const sync = () => {
        const peekButton = drawer.querySelector('#cartFooterExpressPeekBtn');
        if (peekButton) peekButton.setAttribute('aria-expanded', 'true');
        if (typeof drawer.hydrateAcceleratedCheckout === 'function') drawer.hydrateAcceleratedCheckout();
        if (typeof drawer.scheduleExpressWalletIconSync === 'function') drawer.scheduleExpressWalletIconSync();
      };
      sync();
      requestAnimationFrame(sync);
    }

    connectedCallback() {
      // Re-attach the submit listener every time the element is connected.
      // Why: code outside this class (e.g. the desktop layout reorganizer
      // __cadApplyPdpCols in main-product.liquid, which runs after a
      // linked-options variant swap) calls parent.appendChild(this) to move
      // the product-form into a different column. That move fires
      // disconnectedCallback → removeEventListener, leaving the form with
      // NO submit handler when reconnected. Clicking "Add to cart" then
      // does a native POST to /cart/add → browser redirect to /cart.
      // remove+add is idempotent (no listener accumulation) and safe.
      if (this.form && this._boundSubmit) {
        this.form.removeEventListener('submit', this._boundSubmit, true);
        this.form.addEventListener('submit', this._boundSubmit, true);
      }
      if (this.submitButton) {
        ['pointerdown', 'touchstart'].forEach((eventName) => {
          this.submitButton.removeEventListener(eventName, this._boundWarmCartDrawer);
          this.submitButton.addEventListener(eventName, this._boundWarmCartDrawer, { passive: true });
        });
      }
    }

    disconnectedCallback() {
      // Clean up the submit listener so the form node can be GC'd when
      // the section gets removed/replaced. Without this, every
      // product-form ever rendered stays referenced by the listener
      // and accumulates over long sessions.
      // connectedCallback re-attaches if this was just a DOM move.
      if (this.form && this._boundSubmit) {
        this.form.removeEventListener('submit', this._boundSubmit, true);
      }
      if (this.submitButton) {
        ['pointerdown', 'touchstart'].forEach((eventName) => {
          this.submitButton.removeEventListener(eventName, this._boundWarmCartDrawer);
        });
      }
    }

    // Set up live validation for quantity input
    setupQuantityValidation() {
      const quantityInput = this.form.querySelector('input[name="quantity"]');
      if (!quantityInput) return;

      const validateQuantity = () => {
        const variantData = this.getVariantInventoryData();
        if (!variantData || !this.shouldEnforceInventory(variantData)) return;

        const requestedQty = parseInt(quantityInput.value) || 1;
        const inventoryQty = variantData.inventory_quantity || 0;
        
        // Get cart quantity from data attribute if available
        const cartQty = parseInt(quantityInput.dataset.cartQuantity) || 0;
        const remainingQty = Math.max(0, inventoryQty - cartQty);

        if (requestedQty > remainingQty) {
          if (remainingQty <= 0) {
            this.handleErrorMessage('Maximum quantity reached');
          } else {
            this.handleErrorMessage(`Only ${remainingQty} available.`);
          }
        } else {
          this.handleErrorMessage(); // Clear error
        }
      };

      quantityInput.addEventListener('input', validateQuantity);
      quantityInput.addEventListener('change', validateQuantity);
    }

    // Get variant inventory data from page
    getVariantInventoryData() {
      const variantId = this.form.querySelector('[name="id"]')?.value;
      if (!variantId) return null;

      // First, try to get from quantity input data attributes (updated by onVariantChange)
      const quantityInput = this.form.querySelector('input[name="quantity"]');
      if (quantityInput && quantityInput.dataset.inventoryQuantity !== undefined) {
        const inventoryQty = parseInt(quantityInput.dataset.inventoryQuantity);
        if (!isNaN(inventoryQty)) {
          // Note: Don't default inventory_management to 'shopify' - if it's empty/null, 
          // it means no inventory tracking (e.g., gift cards, digital products)
          const mgmt = quantityInput.dataset.inventoryManagement;
          return {
            inventory_quantity: inventoryQty,
            inventory_policy: quantityInput.dataset.inventoryPolicy || 'deny',
            inventory_management: (mgmt && mgmt.length > 0) ? mgmt : null
          };
        }
      }

      // Try to get from #product-variants-data or #product-variants JSON
      const variantsScript = document.getElementById('product-variants-data') || document.getElementById('product-variants');
      if (variantsScript) {
        try {
          const variantsData = JSON.parse(variantsScript.textContent);
          if (variantsData[variantId]) {
            const data = variantsData[variantId];
            return {
              inventory_quantity: data.q,
              inventory_policy: data.p,
              // data.m: true = 'shopify', false/undefined/null = null (no tracking)
              inventory_management: data.m ? 'shopify' : null
            };
          }
        } catch (e) {
          console.error('Error parsing variant data:', e);
        }
      }

      // Try to get from variant-selects/variant-radios (but note: this won't have inventory_quantity)
      const variantSelects = document.querySelector('variant-selects, variant-radios');
      if (variantSelects && variantSelects.currentVariant) {
        return {
          inventory_quantity: variantSelects.currentVariant.inventory_quantity,
          inventory_policy: variantSelects.currentVariant.inventory_policy,
          inventory_management: variantSelects.currentVariant.inventory_management
        };
      }

      return null;
    }

    // Check if we should enforce inventory limits
    shouldEnforceInventory(variantData) {
      // Don't enforce if no inventory management or if overselling is allowed
      if (!variantData) return false;
      if (variantData.inventory_policy === 'continue') return false;
      if (variantData.inventory_management === null || variantData.inventory_management === undefined) return false;
      return true;
    }

    // Fetch current cart quantity for a variant
    async getCartQuantityForVariant(variantId) {
      try {
        const cart = typeof window.getCartSnapshot === 'function'
          ? await window.getCartSnapshot()
          : null;
        if (!cart) return 0;
        const item = cart.items.find(item => String(item.variant_id) === String(variantId));
        return item ? item.quantity : 0;
      } catch (e) {
        console.error('Error fetching cart:', e);
        return 0;
      }
    }

    // Transform Shopify error messages to user-friendly format
    formatErrorMessage(message) {
      if (!message) return 'Error adding to cart.';
      
      // Common Shopify inventory error patterns
      const patterns = [
        { regex: /maximum\s+quantity/i, msg: () => 'You already have the maximum available quantity in your cart.' },
        { regex: /all\s+(\d+)\s+.*are in your cart/i, msg: (m) => `Maximum quantity reached All ${m[1]} are in your cart.` },
        { regex: /only\s+(\d+)\s+.*available/i, msg: (m) => `Only ${m[1]} available.` },
        { regex: /cannot\s+add\s+more/i, msg: () => 'Maximum quantity reached' },
        { regex: /not\s+enough\s+.*in\s+stock/i, msg: () => 'Not enough in stock.' },
        { regex: /inventory/i, msg: () => 'Maximum quantity reached' }
      ];

      for (const pattern of patterns) {
        const match = message.match(pattern.regex);
        if (match) {
          return pattern.msg(match);
        }
      }

      return message;
    }

    async onSubmitHandler(evt) {
      evt.preventDefault();
      if (!this.form) return;
      const analyticsButtonLocation = this.form.dataset.analyticsButtonLocation === 'sticky' ? 'sticky'
        : this.closest('.product-card-wrapper, quick-add-modal') ? 'card' : 'main';
      delete this.form.dataset.analyticsButtonLocation;
      
      // Refresh cart reference at submit time - cart-drawer may not exist yet when constructor runs (DOM order)
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      
      // Prevent double-clicks
      if (this._isSubmitting) return;
      if (this.submitButton && this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this._isSubmitting = true;
      this.handleErrorMessage(); // Clear previous errors

      if (this.submitButton) {
        // Don't set minWidth in pro_qtyside - it causes button to wrap below qty (flex-wrap)
        const inProQtyside = this.submitButton.closest('.pro_qtyside');
        if (!inProQtyside) {
          const btnRect = this.submitButton.getBoundingClientRect();
          this.submitButton.style.minWidth = btnRect.width + 'px';
          this.submitButton.style.minHeight = btnRect.height + 'px';
        }
        
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
      }
      
      const spinner = this.querySelector('.loading-overlay__spinner');
      if (spinner) spinner.classList.remove('hidden');

      // Check if required functions are available. Reference `routes`
      // through `window.` so an undeclared global doesn't throw
      // ReferenceError (`routes?.x` doesn't suppress undeclared-identifier
      // errors - only null/undefined member access).
      if (typeof fetchConfig === 'undefined' || !window.routes || !window.routes.cart_add_url) {
        console.error('product-form: fetchConfig or routes not available');
        this.handleErrorMessage('Page not fully loaded - please refresh and try again');
        this.resetButtonState(spinner);
        return;
      }

      // The coordinator prepares purchase terms once, at dispatch. Shopify's
      // native add endpoint merges the exact variant/properties/selling-plan
      // identity and owns the successful product_added_to_cart event.
      const formData = new FormData(this.form);

      if (this.cart && typeof this.cart.setActiveElement === 'function') {
        this.cart.setActiveElement(document.activeElement);
      }

      /* AppleCare+ add-on: when the protection toggle is checked, capture the
         linked AppleCare+ variant so it's added as a NESTED cart line
         (parent_id = device variant). See snippets/alasil-applecare.liquid. */
      const addonInput = this.querySelector('[data-applecare-input]');
      const addonVariantId = addonInput && addonInput.checked && addonInput.value
        ? parseInt(addonInput.value, 10)
        : null;
      const requestedQty = parseInt(formData.get('quantity'), 10) || 1;
      const requestedCartDelta = requestedQty * (addonVariantId ? 2 : 1);

      /* Do not serialize visual feedback behind the cart snapshot, mutation,
         section HTML, or optional drawer enhancements. Show the drawer's
         loading state immediately, then reconcile it with Shopify's response. */
      this.beginCartDrawerFeedback(this.getCartFeedbackItem());

      /* PDP purchase forms open the cart drawer after Shopify confirms the
         mutation. Updating the header badge optimistically here made the
         count jump while the CTA was still loading, and a rejected inventory
         request visibly added then removed the item. Keep PDP feedback on one
         authoritative success boundary: settleCartBubble() updates the badge
         in the same turn that resetButtonState() ends the CTA loading state.

         Inline/quick-add forms that deliberately keep the drawer closed retain
         the optimistic count, where immediate feedback is still useful. */
      const shouldUpdateBadgeOptimistically = this.dataset.autoOpenCartDrawer === 'false';
      this._optimisticCartDelta = shouldUpdateBadgeOptimistically ? requestedCartDelta : 0;
      if (this._optimisticCartDelta) this.adjustCartBubble(this._optimisticCartDelta);

      try {
        const sectionsList = this.cart
          ? (this.cart.getSectionsToRender?.()?.map(s => s.id)?.join(',') || 'cart-drawer,cart-icon-bubble')
          : 'cart-drawer,cart-icon-bubble';

        let response;
        if (addonVariantId) {
          /* Nested cart lines: device (parent) + AppleCare+ (child).
             parent_id references the parent's variant id within this request. */
          const deviceId = parseInt(formData.get('id'), 10);
          const qty = parseInt(formData.get('quantity'), 10) || 1;

          // Preserve the exact device terms, including hidden preorder data.
          const deviceItem = window.AlAsilPurchaseOptions
            ? window.AlAsilPurchaseOptions.fromFormData(formData)
            : { id: deviceId, quantity: qty, properties: Object.fromEntries(
                [...formData.entries()].filter(([key]) => /^properties\[.+\]$/.test(key))
                  .map(([key, value]) => [key.slice(11, -1), value])) };
          if (formData.get('selling_plan')) deviceItem.selling_plan = formData.get('selling_plan');
          if (formData.get('preorder_requires_review') === 'true') deviceItem.preorder_requires_review = true;

          const body = {
            items: [
              deviceItem,
              { id: addonVariantId, quantity: qty, parent_id: deviceId }
            ],
            sections: sectionsList,
            sections_url: window.location.pathname
          };

          response = await window.cartMutationFetch(`${window.routes.cart_add_url}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify(body)
          });
        } else {
          // FormData: automatically includes all form fields (id, quantity,
          // properties, gift card recipient, selling plan, CSRF token, etc.)
          formData.append('sections', sectionsList);
          formData.append('sections_url', window.location.pathname);

          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];
          config.body = formData;

          response = await window.cartMutationFetch(`${window.routes.cart_add_url}`, config);
        }
        let data;
        try {
          data = await response.json();
        } catch(e) {
          data = { parseError: true };
        }

        if (data.parseError) {
          this.rollbackOptimisticCartBubble();
          await this.cancelCartDrawerFeedback();
          this.handleErrorMessage('Server error - please try again');
          this.resetButtonState(spinner);
          return;
        }

        if (!data.status && response.ok) {
          try { window.AlAsilCartAddContext?.(analyticsButtonLocation, formData.get('id'), data); } catch (_) {}
        }
        if (data.status || !response.ok) {
          this.rollbackOptimisticCartBubble();
          await this.cancelCartDrawerFeedback();
          // Error response from Shopify
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: data.description,
              message: data.message
            });
            }
          
          const errorMsg = this.formatErrorMessage(data.description || data.message);
          this.handleErrorMessage(errorMsg);
            this.error = true;
          
          // DON'T open cart drawer on error - just show the error message
          // The user will see the error near the add-to-cart button
          } else if (!this.cart) {
          // No cart drawer: stay on page, update cart icon, show success (no redirect)
            this.settleCartBubble(data);
            const bubble = document.getElementById('cart-icon-bubble');
            if (bubble) {
              let count = data.item_count;
              let total = data.total_price;
              if (count === undefined && data.sections && data.sections['cart-icon-bubble']) {
                const tmp = document.createElement('div');
                tmp.innerHTML = data.sections['cart-icon-bubble'];
                const cEl = tmp.querySelector('.wbhcartitem');
                if (cEl) count = cEl.textContent.trim();
                if (total === undefined) { const tEl = tmp.querySelector('.wbcarthtotal strong'); if (tEl) total = tEl.textContent.trim(); }
              }
              if (count !== undefined) bubble.querySelectorAll('.wbhcartitem, .header__cart-count').forEach(el => { el.textContent = count; });
              if (total !== undefined) bubble.querySelectorAll('.wbcarthtotal strong').forEach(el => { el.textContent = typeof total === 'number' && typeof window.Shopify?.formatMoney === 'function' ? window.Shopify.formatMoney(total) : total; });
            }
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', cartData: Object.assign({}, data, window.cartSnapshotFromState?.(data)), productVariantId: formData.get('id') });
            }
            if (window.showCartMessage) window.showCartMessage({ type: 'success', text: 'Added to cart' });
            return;
        } else {
          // Success
          this.error = false;
          this.settleCartBubble(data);
          /* Shopify accepted the mutation and the visible badge is already
             authoritative. Do not keep the button busy while the deferred
             drawer scripts finish loading and hydrating. */
          this.resetButtonState(spinner);
          
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              cartData: Object.assign({}, data, window.cartSnapshotFromState?.(data)),
              productVariantId: formData.get('id')
            });
          }

          this.cart = await this.getReadyCartDrawer();
          if (this.cart && typeof this.cart.renderContents === 'function') {
            this.prepareCartDrawerForPurchase();
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener('modalClosed', () => {
                setTimeout(() => { 
                  if (this.cart && typeof this.cart.renderContents === 'function') {
                    this.cart.renderContents(this.getCartRenderState(data));
                  }
                });
              }, { once: true });
              if (typeof quickAddModal.hide === 'function') {
                quickAddModal.hide(true);
              }
            } else {
              // Use sections from add response (Shopify returns them when passed in request) - no extra fetch
              this.cart.renderContents(this.getCartRenderState(data));
            }
          }
        }
      } catch (e) {
        this.rollbackOptimisticCartBubble();
        await this.cancelCartDrawerFeedback();
        console.error('Add to cart error:', e);
        this.handleErrorMessage('Network error. Please try again.');
        this.error = true;
        // Publish cart error event for network errors too
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            errors: 'Network error',
            message: 'Network error. Please try again.'
          });
        }
      } finally {
        this.resetButtonState(spinner);
      }
    }

    // Refresh cart sections after partial add or error
    async refreshCartSections() {
      this.cart = await refreshCartElement();
      if (!this.cart || typeof this.cart.getSectionsToRender !== 'function') return;
      
      try {
        const sections = this.cart.getSectionsToRender().map(section => section.id || section.section);
        const sectionsUrl = `${window.location.pathname}?sections=${sections.join(',')}`;
        
        const response = await fetch(sectionsUrl);
        const sectionsData = await response.json();
        
        // Build a response object that renderContents expects
        const cartResponse = {
          sections: sectionsData
        };
        
        if (typeof this.cart.renderContents === 'function') {
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener('modalClosed', () => {
              setTimeout(() => {
                if (this.cart && typeof this.cart.renderContents === 'function') {
                  this.cart.renderContents(this.getCartRenderState(cartResponse));
                }
              });
            }, { once: true });
            if (typeof quickAddModal.hide === 'function') {
              quickAddModal.hide(true);
            }
          } else {
            this.cart.renderContents(this.getCartRenderState(cartResponse));
          }
        }
        
        // Publish cart update event
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form' });
        }
      } catch (e) {
        console.error('Error refreshing cart sections:', e);
      }
    }

    // Reset button state - always re-enable
    resetButtonState(spinner) {
      this._isSubmitting = false;
      
          if (this.submitButton) {
            this.submitButton.classList.remove('loading');
        this.submitButton.removeAttribute('aria-disabled');
          }
      
          if (this.cart && this.cart.classList.contains('is-empty')) {
            this.cart.classList.remove('is-empty');
          }
      
          if (spinner) spinner.classList.add('hidden');
      
      // Reset error flag for next attempt
      this.error = false;
    }

    handleErrorMessage(errorMessage = false) {
      if (this.hideErrors) return;

      // Clear any existing auto-hide timeout
      if (this._errorTimeout) {
        clearTimeout(this._errorTimeout);
        this._errorTimeout = null;
      }

      // Try to find existing error wrapper
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      
      // If no error wrapper exists, create one dynamically
      if (!this.errorMessageWrapper && errorMessage) {
        this.errorMessageWrapper = document.createElement('div');
        this.errorMessageWrapper.className = 'product-form__error-message-wrapper';
        this.errorMessageWrapper.setAttribute('role', 'alert');
        this.errorMessageWrapper.setAttribute('aria-live', 'polite');
        this.errorMessageWrapper.setAttribute('aria-atomic', 'true');
        this.errorMessageWrapper.innerHTML = `
          <svg aria-hidden="true" focusable="false" class="icon icon-error" viewBox="0 0 13 13">
            <circle cx="6.5" cy="6.5" r="5.5" stroke="currentColor" stroke-width="1" fill="none"></circle>
            <line x1="6.5" y1="4" x2="6.5" y2="7" stroke="currentColor" stroke-width="1"></line>
            <circle cx="6.5" cy="9" r="0.5" fill="currentColor"></circle>
          </svg>
          <span class="product-form__error-message"></span>
        `;
        
        // Insert after submit button or at end of form buttons
        const buttonsContainer = this.querySelector('.product-form__buttons');
        if (buttonsContainer) {
          buttonsContainer.appendChild(this.errorMessageWrapper);
        } else {
          this.form.appendChild(this.errorMessageWrapper);
        }
      }
      
      if (!this.errorMessageWrapper) return;
      
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage && this.errorMessage) {
        this.errorMessage.textContent = errorMessage;
        
        // Keep actionable errors visible long enough to read and understand.
        this._errorTimeout = setTimeout(() => {
          this.errorMessageWrapper.setAttribute('hidden', '');
        }, 5000);
      }
    }
  });
}

// Backup handler for forms that are not inside <product-form> component
// but have data-type="add-to-cart-form"
// product-form.js may be included multiple times per page (e.g. main product + upsell card); register once.
if (!window.__productFormBackupBound) {
  window.__productFormBackupBound = true;
  (function () {
  // Track submission state PER FORM rather than via a single module-scoped
  // `isSubmitting` flag. Previously two concurrent backup-handled forms
  // (e.g. two upsell widgets submitted in quick succession) blocked each
  // other because the first lock prevented the second submit entirely.
  const submittingForms = new WeakSet();
  const isFormSubmitting = (form) => submittingForms.has(form);
  const setFormSubmitting = (form, value) => {
    if (!form) return;
    if (value) submittingForms.add(form);
    else submittingForms.delete(form);
  };

  // Transform Shopify error messages to user-friendly format
  const formatErrorMessage = (message) => {
    if (!message) return 'Error adding to cart.';
    
    const patterns = [
      { regex: /maximum\s+quantity/i, msg: () => 'You already have the maximum available quantity in your cart.' },
      { regex: /all\s+(\d+)\s+.*are in your cart/i, msg: (m) => `Maximum quantity reached All ${m[1]} are in your cart.` },
      { regex: /only\s+(\d+)\s+.*available/i, msg: (m) => `Only ${m[1]} available.` },
      { regex: /cannot\s+add\s+more/i, msg: () => 'Maximum quantity reached' },
      { regex: /not\s+enough\s+.*in\s+stock/i, msg: () => 'Not enough in stock.' },
      { regex: /inventory/i, msg: () => 'Maximum quantity reached' }
    ];

    for (const pattern of patterns) {
      const match = message.match(pattern.regex);
      if (match) {
        return pattern.msg(match);
      }
    }

    return message;
  };

  // Track error timeout per form
  const errorTimeouts = new WeakMap();

  // Show error message, create wrapper if needed
  const showError = (form, message) => {
    // Clear existing timeout
    if (errorTimeouts.has(form)) {
      clearTimeout(errorTimeouts.get(form));
    }

    let errorWrapper = form.querySelector('.product-form__error-message-wrapper');
    
    if (!errorWrapper) {
      errorWrapper = document.createElement('div');
      errorWrapper.className = 'product-form__error-message-wrapper';
      errorWrapper.setAttribute('role', 'alert');
      errorWrapper.setAttribute('aria-live', 'polite');
      errorWrapper.setAttribute('aria-atomic', 'true');
      errorWrapper.innerHTML = `
        <svg aria-hidden="true" focusable="false" class="icon icon-error" viewBox="0 0 13 13">
          <circle cx="6.5" cy="6.5" r="5.5" stroke="currentColor" stroke-width="1" fill="none"></circle>
          <line x1="6.5" y1="4" x2="6.5" y2="7" stroke="currentColor" stroke-width="1"></line>
          <circle cx="6.5" cy="9" r="0.5" fill="currentColor"></circle>
        </svg>
        <span class="product-form__error-message"></span>
      `;
      
      const buttonsContainer = form.querySelector('.product-form__buttons');
      if (buttonsContainer) {
        buttonsContainer.appendChild(errorWrapper);
      } else {
        form.appendChild(errorWrapper);
      }
    }
    
    const errorMessage = errorWrapper.querySelector('.product-form__error-message');
    if (errorMessage) {
      errorWrapper.hidden = false;
      errorMessage.textContent = message;
      
      // Keep actionable errors visible long enough to read and understand.
      const timeout = setTimeout(() => {
        errorWrapper.hidden = true;
      }, 5000);
      errorTimeouts.set(form, timeout);
    }
  };

  // Reset button state (per-form: caller passes the form too)
  const resetButtonState = (submitButton, spinner, form) => {
    if (form) setFormSubmitting(form, false);
    if (submitButton) {
      submitButton.classList.remove('loading');
      submitButton.removeAttribute('aria-disabled');
      submitButton.style.minWidth = '';
      submitButton.style.minHeight = '';
    }
    if (spinner) spinner.classList.add('hidden');
  };

  const handleSubmit = async (e) => {
    const form = e.target.closest('form[data-type="add-to-cart-form"]');
    if (!form) return;
    
    // Skip if form is already handled by product-form component
    if (form.closest('product-form')) return;
    
    // Skip if form doesn't have action="/cart/add"
    if (!form.action || !form.action.includes('/cart/add')) return;
    
    e.preventDefault();
    const analyticsButtonLocation = form.dataset.analyticsButtonLocation === 'sticky' ? 'sticky'
      : form.closest('.product-card-wrapper, quick-add-modal') ? 'card' : 'main';
    delete form.dataset.analyticsButtonLocation;


    // Prevent double-clicks on THIS form (was a global flag - two concurrent
    // forms used to block each other; now they're independent).
    if (isFormSubmitting(form)) return;

    const submitButton = form.querySelector('[type="submit"]');
    if (submitButton && submitButton.getAttribute('aria-disabled') === 'true') return;

    setFormSubmitting(form, true);
    
    // Enable id input if disabled
    const idInput = form.querySelector('[name=id]');
    if (idInput) idInput.disabled = false;
    
    if (submitButton) {
      // Don't set minWidth in pro_qtyside - it causes button to wrap below qty (flex-wrap)
      const inProQtyside = submitButton.closest('.pro_qtyside');
      if (!inProQtyside) {
        const btnRect = submitButton.getBoundingClientRect();
        submitButton.style.minWidth = btnRect.width + 'px';
        submitButton.style.minHeight = btnRect.height + 'px';
      }
      
      submitButton.setAttribute('aria-disabled', 'true');
      submitButton.classList.add('loading');
    }
    
    const spinner = form.querySelector('.loading-overlay__spinner');
    if (spinner) spinner.classList.remove('hidden');
    
    // Check if required functions are available
    if (typeof fetchConfig === 'undefined' || !window.routes || !window.routes.cart_add_url) {
      console.error('product-form: fetchConfig or routes not available');
      showError(form, 'Page not fully loaded - please refresh and try again');
      resetButtonState(submitButton, spinner, form);
      return;
    }
    
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    const formData = new FormData(form);

    let cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    if (cart && typeof cart.getSectionsToRender === 'function') {
      formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
      formData.append('sections_url', window.location.pathname);
      if (typeof cart.setActiveElement === 'function') {
        cart.setActiveElement(document.activeElement);
      }
    } else {
      formData.append('sections', 'cart-drawer,cart-icon-bubble');
      formData.append('sections_url', window.location.pathname);
    }
    config.body = formData;

    const variantId = formData.get('id');
    if (!variantId) {
      resetButtonState(submitButton, spinner, form);
      return;
    }

    try {
      const response = await window.cartMutationFetch(`${window.routes.cart_add_url}`, config);
      let data;
      try {
        data = await response.json();
      } catch (parseErr) {
        showError(form, 'Server error - please try again');
        resetButtonState(submitButton, spinner, form);
        return;
      }

      if (!data.status && response.ok) {
        try { window.AlAsilCartAddContext?.(analyticsButtonLocation, formData.get('id'), data); } catch (_) {}
      }
      if (data.status || !response.ok) {
        // Error response - DON'T open cart drawer, just show error
        const errorMsg = formatErrorMessage(data.description || data.message);
        showError(form, errorMsg);
          
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: formData.get('id'),
            errors: data.description,
            message: data.message
            });
          }
        
        // DON'T open cart drawer on error - just show the error message
        // The user will see the error near the add-to-cart button
        } else if (!cart) {
          // No cart drawer: stay on page, update cart icon (no redirect)
          if (data.sections?.['cart-icon-bubble']) {
            const bubble = document.getElementById('cart-icon-bubble');
            if (bubble) {
              let count = data.item_count;
              let total = data.total_price;
              if (count === undefined) {
                const tmp = document.createElement('div');
                tmp.innerHTML = data.sections['cart-icon-bubble'];
                const cEl = tmp.querySelector('.wbhcartitem');
                if (cEl) count = cEl.textContent.trim();
                if (total === undefined) { const tEl = tmp.querySelector('.wbcarthtotal strong'); if (tEl) total = tEl.textContent.trim(); }
              }
              if (count !== undefined) bubble.querySelectorAll('.wbhcartitem, .header__cart-count').forEach(el => { el.textContent = count; });
              if (total !== undefined) bubble.querySelectorAll('.wbcarthtotal strong').forEach(el => { el.textContent = typeof total === 'number' && typeof window.Shopify?.formatMoney === 'function' ? window.Shopify.formatMoney(total) : total; });
            }
          }
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', cartData: Object.assign({}, data, window.cartSnapshotFromState?.(data)), productVariantId: formData.get('id') });
          }
          if (window.showCartMessage) window.showCartMessage({ type: 'success', text: 'Added to cart' });
          return;
      } else {
        // Success
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            cartData: Object.assign({}, data, window.cartSnapshotFromState?.(data)),
            productVariantId: formData.get('id')
          });
        }

        cart = await refreshCartElement();
        if (cart && typeof cart.renderContents === 'function') {
          cart.renderContents(data);
        }
      }
    } catch (e) {
      console.error('Add to cart error:', e);
      showError(form, 'Network error. Please try again.');
      // Publish cart error event for network errors too
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'product-form',
          productVariantId: formData.get('id'),
          errors: 'Network error',
          message: 'Network error. Please try again.'
        });
      }
    } finally {
      resetButtonState(submitButton, spinner, form);
        if (cart && cart.classList.contains('is-empty')) {
          cart.classList.remove('is-empty');
        }
    }
  };
  
  // Add listener immediately if DOM is ready, otherwise wait for DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      document.addEventListener('submit', handleSubmit, true);
    });
  } else {
    document.addEventListener('submit', handleSubmit, true);
  }
  })();
}

// Set after handlers + custom element are registered; flush early submit blocked by theme.liquid guard (once per page)
if (!window.__productFormModuleReadyDone) {
  window.__productFormModuleReadyDone = true;
  window.__productFormJsReady = true;
  (function flushPendingCartAddForm() {
    const form = window.__pendingCartAddForm;
    if (!form) return;
    window.__pendingCartAddForm = null;
    const analyticsButtonLocation = window.__pendingCartAddButtonLocation;
    window.__pendingCartAddButtonLocation = null;
    if (analyticsButtonLocation === 'sticky') form.dataset.analyticsButtonLocation = 'sticky';
    if (typeof form.requestSubmit === 'function') {
      try {
        form.requestSubmit();
      } catch (e) {
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
    } else {
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
    delete form.dataset.analyticsButtonLocation;
  })();
}

} /* end double-load guard */
